import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Filters } from '../components/backlog/FilterBar';
import type { Id } from '../api/types';
import { filtersFromParams, nextFilterParams, setOrDelete } from './backlog-params';

export const PER_PAGE = 50;

/**
 * Filters, page and selection live in the URL.
 *
 * ⭐ Not because it is fashionable: a backlog filtered to "APFS, Failed on PROD, assigned to me" is
 * the thing people paste to each other, and Back after opening a ticket has to return to the same
 * list rather than to page 1 of everything.
 */
export function useBacklogParams() {
  const [params, setParams] = useSearchParams();

  const filters: Filters = useMemo(() => filtersFromParams(params), [params]);

  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  // ⚠️ Ids are STRINGS (Postgres BIGINT). Kept as strings end to end so `ticket.id === selectedId`
  // cannot quietly become false through a Number() round-trip.
  const selectedId = params.get('ticket');

  /**
   * Which parents are expanded, as `?expanded=7,42`.
   *
   * ⭐ In the URL for the same reason the filters are: the state survives a re-render, a Back out of a
   * ticket, and a reload — and a link to "the backlog with TBS-7 open" is a thing one person sends
   * another. Component state would lose it on every one of those.
   */
  const expanded = useMemo(() => {
    const raw = params.get('expanded');
    if (!raw) return new Set<Id>();
    return new Set(raw.split(',').filter((id) => /^\d+$/.test(id)));
  }, [params]);

  const write = useCallback(
    (mutate: (next: URLSearchParams) => void, options?: { replace?: boolean }) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: options?.replace ?? false });
    },
    [params, setParams],
  );

  /**
   * ⭐ The RULES live in `backlog-params.ts` and are unit-tested there; this is only the plumbing
   *   that hands them the current URL and writes the result back. The sprint-clearing rule in
   *   particular is a claim about shared links, and it is asserted rather than eyeballed.
   */
  const setFilters = useCallback(
    (nextFilters: Filters) => {
      setParams(nextFilterParams(params, nextFilters, filters.projectId), { replace: true });
    },
    [params, setParams, filters.projectId],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      write((next) => setOrDelete(next, 'page', nextPage > 1 ? String(nextPage) : null));
    },
    [write],
  );

  const select = useCallback(
    (ticketId: Id | null) => {
      write((next) => setOrDelete(next, 'ticket', ticketId));
    },
    [write],
  );

  /**
   * ⭐ `replace: true`. Expanding a row is a change of VIEW, not of place: pushing a history entry
   * would make Back collapse one row at a time instead of leaving the backlog, which is the same
   * complaint people have about Back in Jira.
   */
  const toggleExpanded = useCallback(
    (ticketId: Id) => {
      write(
        (next) => {
          const current = new Set((next.get('expanded') ?? '').split(',').filter(Boolean));
          if (current.has(ticketId)) current.delete(ticketId);
          else current.add(ticketId);
          setOrDelete(next, 'expanded', [...current].join(','));
        },
        { replace: true },
      );
    },
    [write],
  );

  return {
    filters,
    page,
    selectedId,
    expanded,
    setFilters,
    setPage,
    select,
    toggleExpanded,
    perPage: PER_PAGE,
  };
}
